import { useState } from 'react'

import { ACCEPT, formatSize, type MaterialKind } from './file'
import { JourneyFrame, Note } from './JourneyChrome'
import { LOCAL_ONLY_TEXT, PANEL, SECONDARY, SEND_ONLY_TEXT } from './ui'

/**
 * Step 1 of the School Material Learning Journey: choose a photo or a PDF on
 * the device, or try a DEMO sample. A chosen file is previewed from a local
 * object URL. It leaves the device only when the learner sends it for reading
 * (Slice 3, `reading` slot); where reading is not available the preview says
 * so instead of pretending to extract the content.
 */

export interface LocalMaterial {
  readonly file: File
  readonly url: string
  readonly kind: MaterialKind
}

const PICK_CARD =
  'flex w-full cursor-pointer items-center gap-4 rounded-3xl border-2 border-slate-200 bg-white p-4 text-left active:bg-slate-50 has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-sky-600 dark:border-slate-800 dark:bg-slate-900 dark:active:bg-slate-800 dark:has-[:focus-visible]:outline-sky-400'

/** A file input inside a large label; resets so the same file can be chosen again. */
function FilePick({
  kind,
  className,
  onPick,
  children,
}: {
  kind: MaterialKind
  className: string
  onPick: (kind: MaterialKind, file: File) => void
  children: React.ReactNode
}): React.JSX.Element {
  return (
    <label className={className}>
      <input
        type="file"
        accept={ACCEPT[kind]}
        className="sr-only"
        onChange={(e) => {
          const file = e.currentTarget.files?.[0]
          e.currentTarget.value = ''
          if (file !== undefined) onPick(kind, file)
        }}
      />
      {children}
    </label>
  )
}

export interface MaterialEntryProps {
  readonly error: string | null
  /** False in builds without DEMO content: no sample path is offered. */
  readonly samplesAvailable: boolean
  /** A chosen file may be sent for reading (Slice 3): the note says when it leaves. */
  readonly canRead: boolean
  readonly onPick: (kind: MaterialKind, file: File) => void
  readonly onSamples: () => void
  readonly onHome: () => void
  /**
   * Upload is closed for this learner (family entitlement off, or restricted):
   * shown instead of the photo and PDF pickers (D-15).
   */
  readonly closed?: React.ReactNode
  /** Lessons the learner can continue (D-17). */
  readonly resume?: React.ReactNode
}

export function MaterialEntry({
  error,
  samplesAvailable,
  canRead,
  closed = null,
  resume = null,
  onPick,
  onSamples,
  onHome,
}: MaterialEntryProps): React.JSX.Element {
  return (
    <JourneyFrame
      step="Tài liệu"
      title="Học từ tài liệu ở trường"
      onBack={null}
      onHome={onHome}
    >
      {resume}
      {closed ?? (
        <p className="text-lg text-slate-700 dark:text-slate-300">
          Con muốn học từ tài liệu nào?
        </p>
      )}

      {error !== null && (
        <p
          role="alert"
          className="rounded-xl bg-rose-50 px-3 py-2 text-lg text-rose-900 dark:bg-rose-950/50 dark:text-rose-100"
        >
          {error}
        </p>
      )}

      <div className="grid gap-3 md:grid-cols-3">
        {closed === null && (
          <>
            <FilePick
              kind="image"
              className={`${PICK_CARD} md:flex-col md:items-start`}
              onPick={onPick}
            >
              <span className="text-4xl" aria-hidden>
                📷
              </span>
              <span className="flex flex-col">
                <span className="text-lg font-semibold">Chọn ảnh</span>
                <span className="text-base text-slate-600 dark:text-slate-400">
                  Ảnh bài tập, trang sách
                </span>
              </span>
            </FilePick>
            <FilePick
              kind="pdf"
              className={`${PICK_CARD} md:flex-col md:items-start`}
              onPick={onPick}
            >
              <span className="text-4xl" aria-hidden>
                📄
              </span>
              <span className="flex flex-col">
                <span className="text-lg font-semibold">Chọn PDF</span>
                <span className="text-base text-slate-600 dark:text-slate-400">
                  Phiếu bài tập dạng PDF
                </span>
              </span>
            </FilePick>
          </>
        )}
        {samplesAvailable && (
          <button
            type="button"
            className={`${PICK_CARD} border-fuchsia-200 md:flex-col md:items-start dark:border-fuchsia-900`}
            onClick={onSamples}
          >
            <span className="text-4xl" aria-hidden>
              🧪
            </span>
            <span className="flex flex-col">
              <span className="text-lg font-semibold">Thử tài liệu mẫu</span>
              <span className="text-base text-slate-600 dark:text-slate-400">
                Đi hết các bước với bài mẫu (DEMO)
              </span>
            </span>
          </button>
        )}
      </div>

      {closed === null && (
        <Note tone="info">
          {canRead ? SEND_ONLY_TEXT : LOCAL_ONLY_TEXT} Ảnh: JPG, PNG, WEBP, HEIC. PDF. Tối
          đa 10 MB.
        </Note>
      )}
    </JourneyFrame>
  )
}

export interface FilePreviewProps {
  readonly material: LocalMaterial
  readonly error: string | null
  /** Send for reading, or the honest "not available" state (Slice 3). */
  readonly reading: React.ReactNode
  readonly canRead: boolean
  readonly onPick: (kind: MaterialKind, file: File) => void
  readonly onRemove: () => void
  readonly onHome: () => void
}

export function FilePreview({
  material,
  error,
  reading,
  canRead,
  onPick,
  onRemove,
  onHome,
}: FilePreviewProps): React.JSX.Element {
  const { file, url, kind } = material
  const [broken, setBroken] = useState<string | null>(null)
  const imageBroken = broken === url

  return (
    <JourneyFrame
      step="Tài liệu"
      title={kind === 'image' ? 'Ảnh con đã chọn' : 'PDF con đã chọn'}
      onBack={onRemove}
      onHome={onHome}
    >
      {error !== null && (
        <p
          role="alert"
          className="rounded-xl bg-rose-50 px-3 py-2 text-lg text-rose-900 dark:bg-rose-950/50 dark:text-rose-100"
        >
          {error}
        </p>
      )}

      <section aria-label="Xem trước tài liệu" className={PANEL}>
        <div className="flex items-center gap-3">
          <span className="text-3xl" aria-hidden>
            {kind === 'image' ? '🖼️' : '📄'}
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-lg font-semibold" title={file.name}>
              {file.name}
            </p>
            <p className="text-base text-slate-600 dark:text-slate-400">
              {kind === 'image' ? 'Ảnh' : 'PDF'} · {formatSize(file.size)}
            </p>
          </div>
        </div>

        {kind === 'image' &&
          (imageBroken ? (
            <p
              role="alert"
              className="rounded-xl bg-slate-100 p-4 text-center dark:bg-slate-800"
            >
              Trình duyệt không hiển thị được ảnh này. Con thử chọn ảnh JPG hoặc PNG nhé.
            </p>
          ) : (
            <>
              {/* Natural size, never enlarged; shrunk to fit with its proportions kept. */}
              <div className="flex justify-center rounded-2xl bg-slate-100 p-2 dark:bg-slate-800">
                <img
                  src={url}
                  alt={`Ảnh đã chọn: ${file.name}`}
                  className="h-auto max-h-[60dvh] w-auto max-w-full rounded-xl object-contain"
                  onError={() => {
                    setBroken(url)
                  }}
                />
              </div>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="self-start px-1 text-lg text-sky-800 underline dark:text-sky-300"
              >
                Xem ảnh cỡ thật
              </a>
            </>
          ))}

        {kind === 'pdf' && (
          <>
            <p className="text-base text-slate-700 dark:text-slate-300">
              Nếu khung dưới đây trống hoặc chỉ hiện trang đầu, con mở PDF ở thẻ mới nhé.
            </p>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className={`${SECONDARY} self-start`}
            >
              Mở PDF trong thẻ mới
            </a>
            <object
              data={url}
              type="application/pdf"
              aria-label={`PDF đã chọn: ${file.name}`}
              className="h-[50dvh] w-full rounded-2xl bg-slate-100 dark:bg-slate-800"
            >
              <p className="p-4 text-center">
                Trình duyệt này không xem PDF ngay tại đây. Dùng nút “Mở PDF trong thẻ
                mới” ở trên.
              </p>
            </object>
          </>
        )}
        <p className="text-base text-slate-600 dark:text-slate-400">
          {canRead ? SEND_ONLY_TEXT : LOCAL_ONLY_TEXT}
        </p>
      </section>

      {reading}

      <div className="flex flex-wrap gap-3">
        <FilePick
          kind={kind}
          className={`${SECONDARY} cursor-pointer has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-sky-600`}
          onPick={onPick}
        >
          Đổi tệp khác
        </FilePick>
        <button type="button" className={SECONDARY} onClick={onRemove}>
          Bỏ tệp này
        </button>
      </div>
    </JourneyFrame>
  )
}
